import React, { useState, useEffect } from 'react';
import {
  X,
  Phone,
  MessageSquare,
  Mail,
  Clock,
  FileText,
  FlaskConical,
  Plus,
  Check,
  AlertCircle,
} from 'lucide-react';
import { formatINR } from '@/shared/utils/currency';
import { api } from '@/shared/api/axios';

export interface Client360Data {
  id?: string;
  type?: 'deal' | 'lead' | 'client';
  name: string;
  company?: string;
  phone?: string;
  email?: string;
  city?: string;
  address?: string;
  stage?: string;
  value?: number;
  notes?: string;
}

interface Client360DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  data: Client360Data | null;
  onAction?: (action: 'call' | 'whatsapp' | 'email' | 'quote' | 'followup' | 'sample') => void;
  currentUser?: { displayName?: string; email?: string } | null;
}

export const Client360Drawer: React.FC<Client360DrawerProps> = ({
  isOpen,
  onClose,
  data,
  onAction,
  currentUser,
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'quotations' | 'samples' | 'notes'>('overview');
  const [noteText, setNoteText] = useState('');
  const [isSavingNote, setIsSavingNote] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  useEffect(() => {
    if (data) {
      setNoteText(data.notes || '');
      setSaveStatus('idle');
    }
  }, [data]);

  if (!isOpen || !data) return null;

  const phone = data.phone || '';
  const cleanPhone = phone.replace(/\D/g, '');
  const company = data.company || data.name;
  const repDisplayName = currentUser?.displayName ? `${currentUser.displayName} (You)` : 'You';

  const handleSaveNote = async () => {
    if (!data.id) return;
    setIsSavingNote(true);
    try {
      if (data.type === 'deal') {
        await api.patch(`/api/pipeline/${data.id}`, { notes: noteText });
      }
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2500);
    } catch {
      setSaveStatus('error');
    } finally {
      setIsSavingNote(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-xs transition-opacity animate-in fade-in duration-200">
      {/* Click outside to close */}
      <div className="flex-1" onClick={onClose} />

      {/* Drawer Container */}
      <div className="relative flex h-full w-full max-w-md flex-col justify-between bg-card shadow-2xl transition-transform animate-in slide-in-from-right duration-200 sm:max-w-lg">
        {/* Header */}
        <div>
          <div className="flex items-start justify-between border-b border-outline p-5">
            <div>
              <div className="flex items-center gap-2">
                <span className="rounded bg-primary-container px-2 py-0.5 text-data-xs font-semibold text-primary border border-primary/30">
                  {data.stage ? data.stage.toUpperCase() : 'LEAD PROFILE'}
                </span>
                <span className="text-body-sm text-on-surface-subtle">• RBM Oil Client</span>
              </div>
              <h2 className="mt-1 font-mono text-data-lg font-semibold tabular-nums text-on-surface">{company}</h2>
              {data.name && data.name !== company && (
                <div className="text-body-sm text-on-surface-subtle">Contact: {data.name}</div>
              )}
            </div>

            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-on-surface-subtle hover:bg-surface-container hover:text-on-surface-variant transition cursor-pointer"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Quick Contact Action Strip */}
          <div className="flex items-center gap-2 border-b border-outline bg-surface-variant/70 px-5 py-3">
            {phone ? (
              <a
                href={`tel:${phone}`}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border bg-card py-2 text-body-sm font-semibold text-on-surface-variant shadow-xs hover:bg-success/10 hover:text-success-text hover:border-success/30 transition"
              >
                <Phone className="h-3.5 w-3.5" />
                <span>Call</span>
              </a>
            ) : (
              <button
                type="button"
                disabled
                title="No phone number available"
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border bg-surface-container py-2 text-body-sm font-medium text-on-surface-subtle cursor-not-allowed opacity-60"
              >
                <Phone className="h-3.5 w-3.5" />
                <span>Call</span>
              </button>
            )}

            {cleanPhone ? (
              <a
                href={`https://wa.me/91${cleanPhone}`}
                target="_blank"
                rel="noreferrer"
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border bg-card py-2 text-body-sm font-semibold text-on-surface-variant shadow-xs hover:bg-success/10 hover:text-success-text hover:border-success/30 transition"
              >
                <MessageSquare className="h-3.5 w-3.5 text-success-text" />
                <span>WhatsApp</span>
              </a>
            ) : (
              <button
                type="button"
                disabled
                title="No WhatsApp number available"
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border bg-surface-container py-2 text-body-sm font-medium text-on-surface-subtle cursor-not-allowed opacity-60"
              >
                <MessageSquare className="h-3.5 w-3.5" />
                <span>WhatsApp</span>
              </button>
            )}

            {data.email ? (
              <a
                href={`mailto:${data.email}`}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border bg-card py-2 text-body-sm font-semibold text-on-surface-variant shadow-xs hover:bg-info/10 hover:text-info-text hover:border-info/30 transition"
              >
                <Mail className="h-3.5 w-3.5 text-info-text" />
                <span>Email</span>
              </a>
            ) : (
              <button
                type="button"
                disabled
                title="No email address available"
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-border bg-surface-container py-2 text-body-sm font-medium text-on-surface-subtle cursor-not-allowed opacity-60"
              >
                <Mail className="h-3.5 w-3.5" />
                <span>Email</span>
              </button>
            )}
          </div>

          {/* Tab Navigation */}
          <div className="flex border-b border-outline px-5 text-body-sm font-semibold">
            <button
              type="button"
              onClick={() => setActiveTab('overview')}
              className={`border-b-2 py-3 px-3 transition cursor-pointer ${
                activeTab === 'overview'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-on-surface-subtle hover:text-on-surface'
              }`}
            >
              Overview
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('quotations')}
              className={`border-b-2 py-3 px-3 transition cursor-pointer ${
                activeTab === 'quotations'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-on-surface-subtle hover:text-on-surface'
              }`}
            >
              Quotations
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('samples')}
              className={`border-b-2 py-3 px-3 transition cursor-pointer ${
                activeTab === 'samples'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-on-surface-subtle hover:text-on-surface'
              }`}
            >
              Samples
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('notes')}
              className={`border-b-2 py-3 px-3 transition cursor-pointer ${
                activeTab === 'notes'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-on-surface-subtle hover:text-on-surface'
              }`}
            >
              Notes
            </button>
          </div>

          {/* Drawer Body */}
          <div className="p-5 space-y-4 overflow-y-auto max-h-[calc(100vh-280px)]">
            {activeTab === 'overview' && (
              <>
                {/* Information Card */}
                <div className="rounded-lg border border-border/80 bg-surface-variant/50 p-4 space-y-2.5 text-body-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-on-surface-subtle">Pipeline Value:</span>
                    <span className="font-semibold text-on-surface text-body-md">
                      {data.value ? formatINR(data.value) : '—'}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-on-surface-subtle">Phone:</span>
                    <span className="font-semibold text-on-surface">{phone || '—'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-on-surface-subtle">Email:</span>
                    <span className="text-on-surface">{data.email || '—'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-on-surface-subtle">Territory / City:</span>
                    <span className="text-on-surface">{data.city || 'Not recorded'}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-on-surface-subtle">Assigned BDM:</span>
                    <span className="font-semibold text-primary">{repDisplayName}</span>
                  </div>
                </div>

                {/* Recent Interaction */}
                <div>
                  <h4 className="text-body-sm font-semibold uppercase tracking-wider text-on-surface-subtle mb-2">
                    Engagement & Notes
                  </h4>
                  {data.notes ? (
                    <div className="rounded-lg border border-border bg-card p-3 text-body-sm space-y-2">
                      <div className="flex items-center gap-2 text-on-surface-variant text-data-xs">
                        <Clock className="h-3 w-3" />
                        <span>Latest Activity Record</span>
                      </div>
                      <p className="text-on-surface-variant leading-snug">{data.notes}</p>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-dashed border-border bg-surface-variant/50 p-4 text-center text-body-sm text-on-surface-subtle">
                      No engagement notes on record yet.
                    </div>
                  )}
                </div>
              </>
            )}

            {activeTab === 'quotations' && (
              <div className="space-y-3 text-body-sm">
                <div className="rounded-lg border border-dashed border-border bg-surface-variant/50 p-6 text-center text-body-sm">
                  <FileText className="mx-auto mb-2 h-6 w-6 text-on-surface-subtle" />
                  <p className="font-semibold text-on-surface-variant">No linked quotations on record</p>
                  <p className="mt-1 text-on-surface-variant">Create a commercial quotation for this account.</p>
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      if (onAction) onAction('quote');
                    }}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-body-sm font-semibold text-on-primary shadow-xs hover:bg-primary-dim cursor-pointer"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    <span>Create Quotation</span>
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'samples' && (
              <div className="space-y-3 text-body-sm">
                <div className="rounded-lg border border-dashed border-border bg-surface-variant/50 p-6 text-center text-body-sm">
                  <FlaskConical className="mx-auto mb-2 h-6 w-6 text-on-surface-subtle" />
                  <p className="font-semibold text-on-surface-variant">No active sampling trials</p>
                  <p className="mt-1 text-on-surface-variant">Dispatch a product sample for machine testing.</p>
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      if (onAction) onAction('sample');
                    }}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-body-sm font-semibold text-on-primary shadow-xs hover:bg-primary-dim cursor-pointer"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    <span>Dispatch Sample</span>
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'notes' && (
              <div className="space-y-2.5 text-body-sm">
                <textarea
                  rows={5}
                  placeholder="Add meeting notes, client requirements, or visit outcomes..."
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  className="w-full rounded-lg border border-border p-3 text-body-sm focus:border-primary focus:outline-none"
                />
                <div className="flex items-center justify-between">
                  <div>
                    {saveStatus === 'saved' && (
                      <span className="inline-flex items-center gap-1 text-success-text font-semibold text-body-sm">
                        <Check className="h-3.5 w-3.5" /> Note saved successfully
                      </span>
                    )}
                    {saveStatus === 'error' && (
                      <span className="inline-flex items-center gap-1 text-danger-text font-semibold text-body-sm">
                        <AlertCircle className="h-3.5 w-3.5" /> Error saving note
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={handleSaveNote}
                    disabled={isSavingNote || !data.id}
                    className="rounded-lg bg-primary px-3.5 py-1.5 text-body-sm font-semibold text-on-primary shadow-xs hover:bg-primary-dim disabled:opacity-50 cursor-pointer"
                  >
                    {isSavingNote ? 'Saving...' : 'Save Note'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer Actions */}
        <div className="border-t border-outline p-4 bg-surface-variant flex items-center gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-lg border border-border bg-card py-2.5 text-body-sm font-semibold text-on-surface-variant hover:bg-surface-container transition cursor-pointer"
          >
            Close
          </button>
          <button
            type="button"
            onClick={() => {
              onClose();
              if (onAction) onAction('quote');
            }}
            className="flex-1 rounded-lg bg-primary py-2.5 text-body-sm font-semibold text-on-primary shadow-xs hover:bg-primary-dim transition cursor-pointer"
          >
            Create Quotation
          </button>
        </div>
      </div>
    </div>
  );
};
